import { object, string } from 'yup';
import { BaseMeta, BaseModel } from './base.model';
import { JsonObject } from '../helpers';
import { CONVERSATION_ACCESS_PERMISSION } from './admin-user.model';

export type GroupMeta = BaseMeta & {
  name: string;
  policies: string[];
  isExternal: boolean;
};

// Form value of the role select when the group maps to no admin role.
export const NO_ADMIN_ROLE = 'none';

export type GroupAdminAccess = {
  role?: string;
  permissions: string[];
};

type GroupExtRef = {
  workos: {
    workosGroupId: string;
    workosOrganizationId: string | null;
    workosDirectoryId: string;
    workosCreatedAt: string;
    workosUpdatedAt: string;
  };
};

export class GroupModel extends BaseModel {
  orgId: string;
  name: string;
  description: string;
  externalReferences: GroupExtRef;
  createdAt: string;
  updatedAt: string;
  policies: string[];
  userCount: number;
  duplicateGroupNameIds: string[];
  adminRole: string;
  adminConversationAccess: boolean;

  schema = object({
    name: string().required('Name is required'),
  });

  constructor(props: JsonObject = {}) {
    super(props);

    const data = props as Record<keyof GroupModel, unknown>;

    this.orgId = (data.orgId as string) || '';
    this.name = (data.name as string) || '';
    this.description = (data.description as string) || '';

    this.externalReferences = (data.externalReferences as GroupExtRef) || {
      workos: {
        workosGroupId: '',
        workosOrganizationId: null,
        workosDirectoryId: '',
        workosCreatedAt: '',
        workosUpdatedAt: '',
      },
    };
    this.createdAt = (data.createdAt as string) || '';
    this.updatedAt = (data.updatedAt as string) || '';
    this.policies = (data.policies as string[]) || [];
    this.userCount = (data.userCount as number) || 0;
    this.duplicateGroupNameIds = (data.duplicateGroupNameIds as string[]) || [];

    const adminAccess = props.adminAccess as GroupAdminAccess | null | undefined;
    this.adminRole = adminAccess?.role || NO_ADMIN_ROLE;
    this.adminConversationAccess =
      adminAccess?.permissions?.includes(CONVERSATION_ACCESS_PERMISSION) ?? false;
  }

  get meta(): GroupMeta {
    return {
      id: this.id || '',
      name: this.name,
      policies: this.policies,
      isExternal: this.isExternal,
    };
  }

  get source(): string {
    if (this.isExternal) {
      return 'sync';
    }

    return 'f5-wais';
  }

  get sourceLabel(): string {
    if (this.source === 'sync') {
      return 'Sync';
    }

    return 'Workforce AI Security';
  }

  get isExternal(): boolean {
    return !!this.externalReferences?.workos?.workosGroupId;
  }

  get canDelete(): boolean {
    return !this.isExternal;
  }

  get canEdit(): boolean {
    return !this.isExternal;
  }

  get hasDuplicateName(): boolean {
    return !!this.duplicateGroupNameIds.length;
  }
}
